import { useCallback, useEffect, useState } from 'react'
import {
  ApiError,
  formatDateTime,
  getAudit,
  getHouseCounts,
  getStaff,
  type AuditRow,
  type HouseCount,
  type StaffRow,
} from '../lib/api'
import { Bar, Card, Empty, ErrorBox, Spinner } from '../components/bits'
import { Pager } from '../components/Modal'

const AUDIT_PER_PAGE = 50

/** Houses & courses — the register-by-house breakdown from SCREENS.md §7. */
export function HousesPage() {
  const [houses, setHouses] = useState<HouseCount[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    getHouseCounts()
      .then((h) => setHouses(h ?? []))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false))
  }, [])

  const max = houses.reduce((m, h) => Math.max(m, h.member_count), 0)
  const total = houses.reduce((sum, h) => sum + h.member_count, 0)

  return (
    <div className="stack">
      <div>
        <div className="eyebrow">Register</div>
        <h1 style={{ marginTop: 8 }}>Houses &amp; courses</h1>
        <p className="small" style={{ marginTop: 6 }}>
          Verified Petarians by house. Counts include approved records only.
        </p>
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}

      <Card>
        {loading ? (
          <Spinner />
        ) : total === 0 ? (
          <Empty
            title="No verified records yet"
            body="Once submissions are approved, the distribution across the eight houses appears here."
          />
        ) : (
          <div className="stack" style={{ gap: 'var(--s-5)' }}>
            {houses.map((h) => (
              <div key={h.house}>
                <div className="row" style={{ marginBottom: 6 }}>
                  <span style={{ color: 'var(--text)', fontSize: 13 }}>{h.house}</span>
                  <div className="spacer" />
                  <span className="small muted">
                    {h.member_count}
                    {total > 0 && ` · ${Math.round((h.member_count / total) * 100)}%`}
                  </span>
                </div>
                {/* Guard the divide: max is 0 on an empty register. */}
                <Bar fraction={max === 0 ? 0 : h.member_count / max} />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

/**
 * The audit log — append-only, and readable only by Secretariat accounts.
 *
 * Deletes and updates are blocked by a trigger in the database, so what is shown
 * here is what happened. That is the point of the screen: it is the record you
 * reach for when someone asks who approved a particular Petarian, and when.
 */
export function AuditPage() {
  const [rows, setRows] = useState<AuditRow[]>([])
  const [staff, setStaff] = useState<StaffRow[]>([])
  const [actor, setActor] = useState<string | null>(null)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // The actor filter needs names to choose from; a failure here should narrow
  // the filters, not break the log.
  useEffect(() => {
    getStaff()
      .then((s) => setStaff(s ?? []))
      .catch(() => setStaff([]))
  }, [])

  useEffect(() => {
    setPage(1)
  }, [actor, from, to])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setRows((await getAudit({ actor, from, to, page, perPage: AUDIT_PER_PAGE })) ?? [])
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [actor, from, to, page])

  useEffect(() => {
    void load()
  }, [load])

  const total = rows[0]?.total ?? 0
  const filtered = actor !== null || from !== '' || to !== ''

  function describe(row: AuditRow): string {
    // before_state/after_state are jsonb, so `.status` is `any`. String() on an
    // object yields "[object Object]" — which is what the audit list would have
    // shown for any row whose status was not a bare string. Only render it when
    // it really is one.
    const before = row.before_state?.status
    const after = row.after_state?.status
    if (typeof before === 'string' && typeof after === 'string') {
      return `${before} → ${after}`
    }
    return row.action
  }

  return (
    <div className="stack">
      <div>
        <div className="eyebrow">Append-only</div>
        <h1 style={{ marginTop: 8 }}>Audit log</h1>
        <p className="small" style={{ marginTop: 6 }}>
          Every verification decision, edit and export, with actor and timestamp.
          Entries cannot be changed or removed.
        </p>
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}

      <Card>
        <div className="filter-row">
          <select
            className="input"
            style={{ maxWidth: 240 }}
            value={actor ?? ''}
            onChange={(e) => setActor(e.target.value === '' ? null : e.target.value)}
            aria-label="Filter by who acted"
          >
            <option value="">Anyone</option>
            {staff.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <label className="small muted" htmlFor="audit-from">
            From
          </label>
          <input
            id="audit-from"
            className="input"
            style={{ maxWidth: 170 }}
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <label className="small muted" htmlFor="audit-to">
            To
          </label>
          <input
            id="audit-to"
            className="input"
            style={{ maxWidth: 170 }}
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          {filtered && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setActor(null)
                setFrom('')
                setTo('')
              }}
            >
              Clear
            </button>
          )}
        </div>
      </Card>

      <Card style={{ padding: 'var(--s-5)' }}>
        {loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty
            title={filtered ? 'Nothing matches those filters' : 'Nothing logged yet'}
            body={
              filtered
                ? 'Try a wider date range, or a different person.'
                : 'Decisions and exports appear here as the Secretariat makes them.'
            }
          />
        ) : (
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Action</th>
                <th>Record</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="small" style={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(r.created_at)}
                  </td>
                  <td>{r.actor_name ?? <span className="muted">System</span>}</td>
                  <td>
                    <span className="pill">{describe(r)}</span>
                  </td>
                  <td>
                    {r.target_kit_no ? (
                      <>
                        <span style={{ color: 'var(--text)' }}>{r.target_name}</span>
                        <br />
                        <span className="small muted">KitNo {r.target_kit_no}</span>
                      </>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="small muted">{r.note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <Pager
          page={page}
          perPage={AUDIT_PER_PAGE}
          total={total}
          busy={loading}
          onChange={setPage}
        />
      </Card>
    </div>
  )
}

/**
 * Placeholders for the two sidebar items the handoff names but does not specify.
 *
 * "Our Martyrs" appears in the design's sidebar with a count of 38 but has no
 * entry in DATA-MODEL.md, no endpoint in API-SPEC.md and no acceptance criteria
 * in SCREENS.md. Rather than invent a schema for it, this says so — an honest
 * gap is more useful than a screen that implies a feature exists.
 */
export function NotSpecifiedPage({
  title,
  reason,
}: {
  title: string
  reason: string
}) {
  return (
    <div className="stack">
      <div>
        <div className="eyebrow">Not yet specified</div>
        <h1 style={{ marginTop: 8 }}>{title}</h1>
      </div>
      <Card>
        <Empty title="Needs a specification first" body={reason} />
      </Card>
    </div>
  )
}
